"use client"

import IconClose from "@/app/(site)/_components/Atoms/Icons/IconClose"
import IconPlus from "@/app/(site)/_components/Atoms/Icons/IconPlus"
import IconTagIcon from "@/app/(site)/_components/Atoms/Icons/IconTagIcon"
import {
    fieldValueTextClasses,
    inactiveTextClasses
} from "@/app/(site)/_components/Atoms/Inputs/input-text-classes"
import InputErrors from "@/app/(site)/_components/Atoms/Inputs/InputErrors"
import { Skeleton } from "@/app/(site)/_components/Atoms/Skeleton"
import { Spinner } from "@/app/(site)/_components/Molecules/Spinner"
import { type FontStylesWithoutDot } from "@/config/fontConfig/fontStyles"
import { FOCUS_VISIBLE_BORDER_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import { t } from "@/translations"
import { useState } from "react"

const VOUCHER_INPUT_HEIGHT = 40
const errorId = "voucher-errors"

interface InputVoucherProps {
    langCode: string
    voucherCode: string
    setVoucherCode: (voucherCode: string) => void
    voucherLoading: boolean
    handleAddVoucher: () => void
    handleRemoveVoucher: (voucherCode: string) => void
    filteredVouchers: { description: string; voucher: string }[]
    automaticVouchers?: { name?: string }[]
    errors?: string[]
    setErrors?: (errors: string[]) => void
    typography?: FontStylesWithoutDot
    disabled?: boolean
    hasMaxVouchersMessage?: string
    hasMaxVouchers?: boolean
    isLoadingSelection?: boolean
}

export default function InputVoucher({
    langCode,
    voucherCode,
    setVoucherCode,
    voucherLoading,
    handleAddVoucher,
    handleRemoveVoucher,
    filteredVouchers,
    automaticVouchers = [],
    errors = [],
    setErrors,
    typography = "Trade_Gothic_Text_16_150_0_300",
    disabled = false,
    hasMaxVouchersMessage = "",
    hasMaxVouchers = false,
    isLoadingSelection = false
}: InputVoucherProps) {
    const [isFocused, setIsFocused] = useState<boolean>(false)
    const hasValue = voucherCode.length > 0
    const hasErrors = errors.length > 0
    const showFloatingLabel = isFocused || hasValue

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && voucherCode.trim()) {
            e.preventDefault()
            handleAddVoucher()
        }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setVoucherCode(e.target.value)

        // Clear errors when user starts typing
        if (errors.length > 0 && setErrors) {
            setErrors([])
        }
    }

    return (
        <div className="flex flex-col gap-8">
            <div className="relative">
                <label
                    htmlFor="voucher-code"
                    className={clsx(`relative block !h-[${VOUCHER_INPUT_HEIGHT}px]`, {
                        "cursor-not-allowed": disabled
                    })}
                >
                    <input
                        type="text"
                        id="voucher-code"
                        name="voucher-code"
                        value={voucherCode}
                        onChange={handleInputChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        onKeyDown={handleKeyDown}
                        disabled={disabled || voucherLoading}
                        className={clsx(
                            typography,
                            "w-full rounded-[4px] border bg-col-bg-primary px-12 pb-[3px] pt-[9px] transition-colors duration-200",
                            FOCUS_VISIBLE_BORDER_CLASS,
                            fieldValueTextClasses(hasValue),
                            `!h-[${VOUCHER_INPUT_HEIGHT}px]`,
                            hasErrors
                                ? "border-col-border-alert"
                                : disabled
                                  ? "cursor-not-allowed border-border-neutral opacity-30"
                                  : "border-border-neutral hover:border-black",
                            !hasErrors && isFocused && "border-col-border-strong"
                        )}
                        aria-invalid={hasErrors}
                        aria-describedby={hasErrors ? errorId : undefined}
                    />
                    {isLoadingSelection && (
                        <Skeleton className="absolute left-4 right-4 top-6 z-10 h-[30px]" />
                    )}

                    {/* Floating Label */}
                    <span
                        className={clsx(
                            "pointer-events-none absolute left-13 top-1/2 -translate-y-1/2 select-none pt-3 transition-opacity duration-200 ease-in-out",
                            inactiveTextClasses,
                            showFloatingLabel || hasValue ? "opacity-0" : "opacity-100",
                            typography,
                            (disabled || voucherLoading) && !hasValue && "opacity-30"
                        )}
                    >
                        {hasMaxVouchers
                            ? hasMaxVouchersMessage
                            : t(langCode, "checkout.order_summary.voucher_placeholder")}
                    </span>

                    {/* Apply Button */}
                    <button
                        onClick={handleAddVoucher}
                        disabled={voucherLoading || !voucherCode.trim() || disabled}
                        type="button"
                        className={clsx(
                            "absolute right-0 top-0 flex h-full items-center bg-transparent px-12 pt-3 transition-colors",
                            "Trim_Mono_POC_Text_12_130_-3_400_uppercase",
                            voucherLoading || !voucherCode.trim() || disabled
                                ? "cursor-not-allowed text-col-text-tertiary"
                                : "text-col-text-primary"
                        )}
                    >
                        {voucherLoading ? (
                            <Spinner />
                        ) : (
                            <>
                                <IconPlus className="h-16 w-16" />
                                {t(langCode, "checkout.order_summary.apply")}
                            </>
                        )}
                    </button>
                </label>
                <div className="flex flex-col gap-6 md:flex-row">
                    {hasErrors && (
                        <InputErrors
                            /** Centra's own wording for why it turned the code down. */
                            errors={errors.map(message => ({
                                code: "value_invalid" as const,
                                message
                            }))}
                            id={errorId}
                            isFocused={isFocused && !hasValue}
                            className="w-full flex-1 pl-12 pt-6 md:w-1/3"
                        />
                    )}
                    {(filteredVouchers.length > 0 || automaticVouchers.length > 0) && (
                        <div
                            className={clsx(
                                "flex w-full flex-wrap gap-6 pt-6",
                                hasErrors
                                    ? "right-0 ml-auto !w-full justify-start md:!w-2/3 md:justify-end"
                                    : "justify-start"
                            )}
                        >
                            {filteredVouchers.map(
                                (
                                    voucher: { description: string; voucher: string },
                                    index: number
                                ) => (
                                    <button
                                        key={`voucher-${voucher.voucher}-${index}`}
                                        onClick={() =>
                                            handleRemoveVoucher(voucher.voucher)
                                        }
                                        disabled={voucherLoading}
                                        type="button"
                                        className={clsx(
                                            "flex h-24 w-fit items-center gap-4 bg-col-bg-muted p-4 transition-colors",
                                            voucherLoading
                                                ? "cursor-not-allowed opacity-50"
                                                : "hover:border-col-text-alert hover:bg-col-bg-muted"
                                        )}
                                    >
                                        <span className="flex-shrink-0 p-2">
                                            <IconTagIcon className="h-12 w-12" />
                                        </span>
                                        <span
                                            className={clsx(
                                                "flex items-center pt-3 text-col-text-primary",
                                                "Trade_Gothic_Text_12_150_0_300_uppercase"
                                            )}
                                        >
                                            {voucher.description || voucher.voucher}
                                        </span>
                                        <span className="flex-shrink-0 p-4">
                                            <IconClose className="h-8 w-8 text-col-text-secondary transition-colors hover:text-col-text-alert" />
                                        </span>
                                    </button>
                                )
                            )}
                            {automaticVouchers.map(
                                ({ name }: { name?: string }, index: number) => (
                                    <div
                                        key={`automatic-voucher-${name ?? ""}-${index}`}
                                        className="flex h-24 w-fit items-center gap-4 bg-col-bg-muted p-4"
                                    >
                                        <span className="flex-shrink-0 p-2">
                                            <IconTagIcon className="h-12 w-12" />
                                        </span>
                                        <span
                                            className={clsx(
                                                "flex items-center pt-3 text-col-text-primary",
                                                "Trade_Gothic_Text_12_150_0_300_uppercase"
                                            )}
                                        >
                                            {name}
                                        </span>
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
